/**
 * UTM Parameter Tracking Utility
 * Captures and persists UTM parameters for marketing campaign tracking
 */

const UTM_STORAGE_KEY = "illumi-utm-params";
const UTM_EXPIRY_KEY = "illumi-utm-expiry";
const UTM_EXPIRY_HOURS = 1; // UTM parameters expire after 1 hour

export interface UtmParams {
	"utm-source"?: string;
	"utm-medium"?: string;
	"utm-campaign"?: string;
	"utm-term"?: string;
	"utm-content"?: string;
	referrer?: string;
	"landing-page"?: string;
	timestamp?: string;
}

/**
 * Extract UTM parameters from URL search params
 */
export function extractUtmParams(searchParams: URLSearchParams): UtmParams {
	const utmParams: UtmParams = {};

	// Extract standard UTM parameters (from URL with underscores, store with dashes)
	const utmMapping = {
		utm_source: "utm-source",
		utm_medium: "utm-medium",
		utm_campaign: "utm-campaign",
		utm_term: "utm-term",
		utm_content: "utm-content",
	} as const;

	for (const [urlKey, storageKey] of Object.entries(utmMapping)) {
		const value = searchParams.get(urlKey);
		if (value) {
			utmParams[storageKey as keyof UtmParams] = value;
		}
	}

	// Add referrer if available
	if (document.referrer) {
		utmParams.referrer = document.referrer;
	}

	// Add landing page
	utmParams["landing-page"] = window.location.href;
	utmParams.timestamp = new Date().toISOString();

	return utmParams;
}

/**
 * Check if stored UTM parameters have expired
 */
function isUtmExpired(): boolean {
	const expiryStr = localStorage.getItem(UTM_EXPIRY_KEY);
	if (!expiryStr) return true;

	const expiryDate = new Date(expiryStr);
	return new Date() > expiryDate;
}

/**
 * Save UTM parameters to localStorage
 */
export function saveUtmParams(params: UtmParams): void {
	if (Object.keys(params).length === 0) return;

	// Set expiry date
	const expiryDate = new Date();
	expiryDate.setHours(expiryDate.getHours() + UTM_EXPIRY_HOURS);

	localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
	localStorage.setItem(UTM_EXPIRY_KEY, expiryDate.toISOString());
}

/**
 * Get stored UTM parameters from localStorage
 */
export function getStoredUtmParams(): UtmParams | null {
	if (isUtmExpired()) {
		clearUtmParams();
		return null;
	}

	const stored = localStorage.getItem(UTM_STORAGE_KEY);
	if (!stored) return null;

	try {
		return JSON.parse(stored) as UtmParams;
	} catch (error) {
		console.error("Error parsing stored UTM params:", error);
		clearUtmParams();
		return null;
	}
}

/**
 * Clear stored UTM parameters
 */
export function clearUtmParams(): void {
	localStorage.removeItem(UTM_STORAGE_KEY);
	localStorage.removeItem(UTM_EXPIRY_KEY);
}

/**
 * Capture UTM parameters from current URL if present
 * Only saves if UTM params don't already exist (first-touch attribution)
 */
export function captureUtmParams(): UtmParams | null {
	const searchParams = new URLSearchParams(window.location.search);
	const utmParams = extractUtmParams(searchParams);

	// Only save if we have at least one UTM parameter and no existing params
	const hasUtmParams = Object.keys(utmParams).some((key) =>
		key.startsWith("utm-"),
	);

	if (hasUtmParams) {
		const existingParams = getStoredUtmParams();
		// First-touch attribution: only save if no existing params
		if (!existingParams) {
			saveUtmParams(utmParams);
			return utmParams;
		}
	}

	return getStoredUtmParams();
}
